import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  signal
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { IParams } from "@interfaces/custom-route-params";
import { Store } from "@ngrx/store";
import { ModalService } from "@services/modal.service";
import { MvService } from "@services/mv.service";
import { AppActions } from "@state/actions/app.actions";
import { UserActions } from "@state/actions/user.actions";
import { selectQueryParams } from "@state/selectors/router.selectors";
import { selectToken } from "@state/selectors/user.selectors";
import { forkJoin } from "rxjs";

/** Tipo de nota pedido pelo zemtweb no query param `type`. */
export type TipoNota = "NFE" | "NFSE" | "NFCE";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  token = this.store.selectSignal(selectToken);
  mounted = signal(false);

  queryParams =
    this.store.selectSignal<
      IParams<"token" | "obra" | "type" | "constructionId" | "customerId">
    >(selectQueryParams());

  changeParams$ = toObservable(this.queryParams)
    .pipe(takeUntilDestroyed())
    .subscribe((params) => {
      this.loadToken(params);
    });

  constructor(
    private readonly store: Store,
    private readonly mvService: MvService,
    private readonly modalService: ModalService,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router
  ) {}

  loadToken(params: ReturnType<typeof this.queryParams>) {
    const token = params?.token;
    const currentToken = this.token();

    if (token && token !== currentToken) {
      this.store.dispatch(
        UserActions.loadToken({
          token
        })
      );
    }

    this.mounted.set(Boolean(currentToken || token));

    const tipo = this.resolveTipoNota(params?.type);
    const routeParams = this.getRouteBudgetParams(params);

    if (!routeParams) {
      // NFS-e pedida sem obra: não há tomador para trazer, vai para a lista.
      if (tipo === "NFSE") this.router.navigate(["/hud/nfses"]);
      // NFC-e só existe a partir da obra (v1): sem obra, vai para a lista.
      if (tipo === "NFCE") this.router.navigate(["/hud/nfces"]);
      return;
    }

    this.loadBudget(routeParams.budgetId, routeParams.customerId, tipo);
  }

  /**
   * Carrega o orçamento da obra e abre a emissão. NF-e e NFC-e partem dos
   * itens do orçamento; NFS-e abre com o cliente da obra como tomador e os
   * serviços escolhidos do cadastro fiscal (a obra não tem LC 116/NBS/ISS).
   */
  loadBudget(
    budgetId: number,
    customerId?: number,
    tipoNota: TipoNota = "NFE"
  ) {
    const nome =
      tipoNota === "NFSE" ? "NFS-e" : tipoNota === "NFCE" ? "NFC-e" : "NF-e";

    forkJoin([
      this.mvService.fetchBudget(budgetId),
      this.mvService.fetchObra(budgetId)
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([budget, obra]) => {
          if (customerId && budget?.cliente?.id !== customerId) {
            this.modalService.error(
              `Os dados recebidos para emissão da ${nome} são inválidos para o cliente informado.`
            );
            return;
          }

          const selected = { budget: { ...budget, obra } };

          this.store.dispatch(
            tipoNota === "NFSE"
              ? AppActions.selectBudgetForNfse(selected)
              : tipoNota === "NFCE"
                ? AppActions.selectBudgetForNfce(selected)
                : AppActions.selectBudget(selected)
          );
        },
        error: (error) => {
          this.modalService.error(
            error?.error?.message ?? "Não foi possível carregar o orçamento."
          );
        }
      });
  }

  private resolveTipoNota(type: string | undefined | null): TipoNota {
    const normalized = this.normalizeType(type);

    if (normalized === "NFSE") return "NFSE";
    if (normalized === "NFCE") return "NFCE";

    return "NFE";
  }

  private getRouteBudgetParams(
    params: ReturnType<typeof this.queryParams>
  ): { budgetId: number; customerId?: number } | null {
    if (!params) return null;

    const type = this.normalizeType(params.type);
    // NF-e e NFC-e chegam do zemtweb com constructionId/customerId.
    const usesConstructionId = type === "NFE" || type === "NFCE";

    if (usesConstructionId && params.constructionId) {
      const budgetId = Number(params.constructionId);
      const customerId = Number(params.customerId);

      if (!Number.isFinite(budgetId) || budgetId <= 0) {
        return null;
      }

      return {
        budgetId,
        customerId:
          Number.isFinite(customerId) && customerId > 0 ? customerId : undefined
      };
    }

    const budgetId = Number(params.obra);

    if (!Number.isFinite(budgetId) || budgetId <= 0) {
      return null;
    }

    return {
      budgetId
    };
  }

  private normalizeType(type: string | undefined | null) {
    return (type ?? "").replaceAll(/[^a-z0-9]/gi, "").toUpperCase();
  }
}
